import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth, useMe } from '@/lib/auth'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { Spinner } from '@/components/spinner'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            auto_select?: boolean
            ux_mode?: 'popup' | 'redirect'
          }) => void
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void
        }
      }
    }
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client'

function useGoogleScript() {
  const [ready, setReady] = useState(() => typeof window.google?.accounts !== 'undefined')
  useEffect(() => {
    if (ready) return
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`)
    const script = existing ?? document.createElement('script')
    const onLoad = () => setReady(true)
    if (!existing) {
      script.src = GIS_SRC
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }
    script.addEventListener('load', onLoad)
    return () => script.removeEventListener('load', onLoad)
  }, [ready])
  return ready
}

export function LoginPage() {
  const trpc = useTRPC()
  const { signIn, token } = useAuth()
  const { user } = useMe()
  const location = useLocation()
  const config = useQuery(trpc.auth.webConfig.queryOptions())
  const googleReady = useGoogleScript()
  const buttonRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const googleSignIn = useMutation(
    trpc.auth.googleSignIn.mutationOptions({
      onSuccess: (data) => signIn(data.token, data.user),
      onError: (err) => setError(errorMessage(err, 'Sign-in failed.')),
    }),
  )

  const clientId = config.data?.googleClientId ?? null
  const devSignIn = useMutation(
    trpc.auth.devSignIn.mutationOptions({
      onSuccess: (data) => signIn(data.token, data.user),
      onError: (err) => setError(errorMessage(err, 'Dev sign-in failed.')),
    }),
  )
  useEffect(() => {
    if (!googleReady || !clientId || !buttonRef.current || !window.google) return
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: ({ credential }) => googleSignIn.mutate({ idToken: credential }),
    })
    buttonRef.current.innerHTML = ''
    window.google.accounts.id.renderButton(buttonRef.current, {
      // white pill: Google's dark fill is nearly the card colour
      theme: 'outline',
      size: 'large',
      shape: 'pill',
      text: 'continue_with',
      width: 280,
    })
    // the mutation object identity changes every render; only re-init on config changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleReady, clientId])

  if (user) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from && from !== '/login' ? from : '/dashboard'} replace />
  }
  if (token !== null && user === undefined) {
    // a stored session is being verified: show nothing but a spinner, not the form
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="card w-full max-w-sm p-8 text-center">
        <img src="/brand/logo-tile.png" alt="" className="mx-auto mb-4 size-16 rounded-2xl" />
        <h1 className="font-display text-2xl tracking-wide">Dispence</h1>
        <p className="mt-2 text-sm text-muted">
          Your trading wallet and strategies, in the browser.
        </p>
        <div className="mt-8 flex min-h-12 flex-col items-center justify-center gap-3">
          {config.isPending ? (
            <Spinner />
          ) : clientId === null ? (
            <p className="text-sm text-muted">Google sign-in is not configured on this server yet.</p>
          ) : (
            <div ref={buttonRef} style={{ colorScheme: 'light' }} />
          )}
          {config.data?.devSignIn ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={devSignIn.isPending}
              onClick={() => {
                // ?devChatId=<telegram chat id> signs into that account's linked app account (local only)
                const chatId = Number(new URLSearchParams(window.location.search).get('devChatId'))
                devSignIn.mutate(chatId ? { chatId } : undefined)
              }}
            >
              {devSignIn.isPending ? 'Signing in…' : 'Continue as local dev account'}
            </button>
          ) : null}
        </div>
        {googleSignIn.isPending ? (
          <p className="mt-4 text-sm text-muted">Signing in…</p>
        ) : null}
        {error ? <p className="mt-4 text-sm text-negative">{error}</p> : null}
      </div>
    </div>
  )
}
